'use client';

import type { TFunction } from 'i18next';
import { swissCantonLabel } from '@/lib/countries';
import type { createFormatters } from '@/new_components/company-detail/currencyFormatters';
import type { ColumnDef } from '@tanstack/react-table';
import { LegalStatusBadge } from './LegalStatusBadge';
import { companyCountryCode, generateCompanySlug } from '@/lib/company-utils';
import { cn } from '@/lib/utils';
import { CompanyCountryFlag } from '@/new_components/shared/CompanyCountryFlag';
import type { useLocale } from '@/hooks/use-locale';
import { Checkbox } from '@/components/ui/checkbox';
import type { CompanySearchResult } from '@/lib/actions/company-search';
import { SortIcon } from './ResultsTableParts';
import { columnLabelKey, numberFormatter, secondaryTextClass } from './resultsTableColumns';
import { CompanyNameLink } from './CompanyNameLink';
import { SelectAllCheckbox } from './SelectAllCheckbox';

/** What the column renderers read; CompanyResultsTable memoizes the columns on exactly these. */
export interface ResultColumnsContext {
  t: TFunction<'translation', undefined>;
  /** i18n.language — the column memo is keyed on it. */
  language: string;
  fmt: ReturnType<typeof createFormatters>;
  offset: number;
  sort: string | undefined;
  handleSort: (field: string) => void;
  country: string | undefined;
  locale: ReturnType<typeof useLocale>['locale'];
  results: CompanySearchResult[];
  onOpenPreview: ((euId: string, trigger?: HTMLElement) => void) | undefined;
}

/**
 * The results table's column definitions, in display order. A new column goes
 * into the group builder it sits with (order here is the table's order), plus
 * its default in DEFAULT_COLUMN_VISIBILITY and its label in
 * search.table.* (resultsTableColumns.ts); add it to MOBILE_HIDE unless it
 * belongs on phones.
 */
export function buildResultColumns(ctx: ResultColumnsContext): ColumnDef<CompanySearchResult>[] {
  // Columns to hide on mobile — only Name and City are shown by default.
  const MOBILE_HIDE = new Set([
    'legalStatus',
    'bundesland',
    'rechtsform',
    'industrySector',
    'naceCode',
    'cpvCodes',
    'employees',
    'totalRevenue',
    'profit',
    'domain',
    'email',
    'registerType',
    'registerId',
    'registerCourt',
    'kreis',
    'postalCode',
  ]);
  const defs: ColumnDef<CompanySearchResult>[] = [
    ...anchorColumns(ctx),
    ...statusAndLocationColumns(ctx),
    ...industryAndSizeColumns(ctx),
    ...contactColumns(ctx),
    ...registerColumns(ctx),
  ];
  return defs.map((c) => {
    const key = (c as { accessorKey?: string }).accessorKey;
    if (!key || !MOBILE_HIDE.has(key)) return c;
    const existingMeta =
      (c as { meta?: Record<string, unknown> }).meta ?? {};
    return {
      ...c,
      meta: {
        ...existingMeta,
        headerClassName: cn(
          'hidden lg:table-cell',
          existingMeta.headerClassName as string | undefined,
        ),
        cellClassName: cn(
          'hidden lg:table-cell',
          existingMeta.cellClassName as string | undefined,
        ),
      },
    };
  });
}

/** The anchors: row selection, row number and the company name (always visible). */
function anchorColumns(ctx: ResultColumnsContext): ColumnDef<CompanySearchResult>[] {
  const { t, offset, sort, handleSort, locale, results, onOpenPreview } = ctx;
  return [
    {
      id: 'select',
      header: ({ table }) => (
        <SelectAllCheckbox
          t={t}
          table={table}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          aria-label={t('search.selectRow', 'Select company')}
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(value === true)}
          // The row itself navigates to the company page.
          onClick={(e) => e.stopPropagation()}
        />
      ),
      size: 40,
      enableResizing: false,
    },
    {
      id: 'index',
      header: () => (
        <span className="text-muted-foreground w-8 block text-center">#</span>
      ),
      cell: (info) => (
        <span className="text-muted-foreground w-8 block text-center text-xs">
          {offset + info.row.index + 1}
        </span>
      ),
      size: 50,
      enableResizing: false,
    },
    {
      accessorKey: 'name',
      header: () => (
        <div
          className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
          onClick={() => handleSort('name')}
        >
          <span>{t('search.table.name')}</span>
          <SortIcon field="name" sort={sort} />
        </div>
      ),
      cell: (info) => {
        const company = info.row.original;
        const faviconUrl = (
          company as CompanySearchResult & { faviconUrl?: string }
        ).faviconUrl;
        const slug = generateCompanySlug(
          {
            name: company.name,
            sitz: company.city,
            registerCourt: company.registerCourt,
            registerType: company.registerType,
            registerId: company.registerId,
          },
          locale,
        );
        return (
          <CompanyNameLink
            slug={slug}
            results={results}
            company={company}
            offset={offset}
            onOpenPreview={onOpenPreview}
            faviconUrl={faviconUrl}
            info={info}
          />
        );
      },
      size: 100,
    },
  ];
}

/** Status, seat and legal form. */
function statusAndLocationColumns(ctx: ResultColumnsContext): ColumnDef<CompanySearchResult>[] {
  const { t, language, country } = ctx;
  return [
    {
      accessorKey: 'legalStatus',
      header: () => <span>{t('search.table.legalStatus', 'Status')}</span>,
      cell: (info) => {
        const status = info.getValue() as string;
        if (!status || status === 'active') {
          return (
            <span className={secondaryTextClass}>
              {t('search.sidebar.legalStatusOptions.active', {
                defaultValue: 'Active',
              })}
            </span>
          );
        }
        return <LegalStatusBadge status={status} />;
      },
      size: 110,
    },
    {
      accessorKey: 'city',
      header: () => <span>{t('search.table.city')}</span>,
      // Flag before the city: with the country scope on Global, "Cham"
      // alone could be the one in Bavaria or the one in Zug.
      cell: (info) => {
        const row = info.row.original;
        return (
          <span
            className={`${secondaryTextClass} flex min-w-0 items-center gap-1.5 max-w-[100px] lg:max-w-[150px]`}
          >
            <CompanyCountryFlag
              countryCode={companyCountryCode(row.registerType, row.euId)}
            />
            <span className="truncate">
              {(info.getValue() as string) || '—'}
            </span>
          </span>
        );
      },
      size: 120,
    },
    {
      accessorKey: 'bundesland',
      header: () => (
        <span>
          {t(`search.table.${columnLabelKey('bundesland', 'state', country)}`)}
        </span>
      ),
      cell: (info) => {
        const row = info.row.original;
        const region = info.getValue() as string | null;
        const label = companyCountryCode(row.registerType, row.euId) === 'CH'
          ? swissCantonLabel(region, language)
          : region;
        return (
          <span
            className={`${secondaryTextClass} truncate block max-w-[100px] lg:max-w-[150px]`}
            title={label || undefined}
          >
            {label || '—'}
          </span>
        );
      },
      size: 120,
    },
    {
      accessorKey: 'rechtsform',
      header: () => (
        <span>{t('search.table.rechtsform', 'Legal form')}</span>
      ),
      cell: (info) => {
        const val = info.getValue() as string | null;
        return (
          <span
            className={`${secondaryTextClass} truncate block max-w-[120px] lg:max-w-[180px]`}
            title={val || undefined}
          >
            {val || '—'}
          </span>
        );
      },
      size: 140,
    },
  ];
}

/** Industry and the sortable size figures. */
function industryAndSizeColumns(ctx: ResultColumnsContext): ColumnDef<CompanySearchResult>[] {
  const { t, fmt, sort, handleSort } = ctx;
  return [
    {
      accessorKey: 'industrySector',
      header: () => <span>{t('search.table.industry')}</span>,
      cell: (info) => {
        const val = info.getValue() as string;
        return (
          <span
            className={`${secondaryTextClass} truncate block max-w-[120px] lg:max-w-[200px]`}
            title={val}
          >
            {val || '—'}
          </span>
        );
      },
      size: 150,
    },
    {
      accessorKey: 'naceCode',
      header: () => <span>{t('search.table.naceCode')}</span>,
      cell: (info) => (
        <span className={`${secondaryTextClass} whitespace-nowrap`}>
          {(info.getValue() as string) || '—'}
        </span>
      ),
      size: 110,
    },
    {
      accessorKey: 'employees',
      header: () => (
        <div
          className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
          onClick={() => handleSort('employees')}
        >
          <span>{t('search.table.employees')}</span>
          <SortIcon field="employees" sort={sort} />
        </div>
      ),
      cell: (info) => {
        const val = info.getValue() as number | null;
        if (val === null || val === undefined)
          return <span className="text-muted-foreground text-xs">—</span>;
        return (
          <span className={secondaryTextClass}>
            {numberFormatter.format(val)}
          </span>
        );
      },
      size: 110,
    },
    {
      accessorKey: 'totalRevenue',
      header: () => (
        <div
          className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
          onClick={() => handleSort('revenue')}
        >
          <span>{t('search.table.revenue')}</span>
          <SortIcon field="revenue" sort={sort} />
        </div>
      ),
      cell: (info) => {
        const val = info.getValue() as number | null;
        if (val === null || val === undefined)
          return <span className="text-muted-foreground text-xs">—</span>;
        return (
          <span className={`${secondaryTextClass} whitespace-nowrap`}>
            {fmt.compactCurrency.format(val)}
          </span>
        );
      },
      size: 110,
    },
    {
      accessorKey: 'profit',
      header: () => (
        <div
          className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
          onClick={() => handleSort('profit')}
        >
          <span>{t('search.table.profit')}</span>
          <SortIcon field="profit" sort={sort} />
        </div>
      ),
      cell: (info) => {
        const val = info.getValue() as number | null;
        if (val === null || val === undefined)
          return <span className="text-muted-foreground text-xs">—</span>;
        return (
          <span className={secondaryTextClass}>
            {fmt.compactCurrency.format(val)}
          </span>
        );
      },
      size: 110,
    },
  ];
}

/** Website and e-mail links. */
function contactColumns(ctx: ResultColumnsContext): ColumnDef<CompanySearchResult>[] {
  const { t } = ctx;
  return [
    {
      accessorKey: 'domain',
      header: () => <span>{t('search.table.website')}</span>,
      cell: (info) => {
        const val = info.getValue() as string | null;
        if (!val)
          return <span className="text-muted-foreground text-xs">—</span>;
        return (
          <a
            href={`https://${val}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline truncate block max-w-[120px] lg:max-w-40"
            title={val}
            onClick={(e) => e.stopPropagation()}
          >
            {val}
          </a>
        );
      },
      size: 140,
    },
    {
      accessorKey: 'email',
      header: () => <span>{t('search.table.email')}</span>,
      cell: (info) => {
        const val = info.getValue() as string | null;
        if (!val)
          return <span className="text-muted-foreground text-xs">—</span>;
        return (
          <a
            href={`mailto:${val}`}
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline truncate block max-w-[120px] lg:max-w-[180px]"
            title={val}
            onClick={(e) => e.stopPropagation()}
          >
            {val}
          </a>
        );
      },
      size: 150,
    },
  ];
}

/** Register identifiers, district, postcode and tender CPV codes. */
function registerColumns(ctx: ResultColumnsContext): ColumnDef<CompanySearchResult>[] {
  const { t, language, country } = ctx;
  return [
    {
      accessorKey: 'registerType',
      header: () => <span>{t('search.table.registerType')}</span>,
      cell: (info) => (
        <span className={`${secondaryTextClass} whitespace-nowrap`}>
          {(info.getValue() as string) || '—'}
        </span>
      ),
      size: 110,
    },
    {
      accessorKey: 'registerId',
      header: () => (
        <span>
          {t(
            `search.table.${columnLabelKey('registerId', 'registerId', country)}`,
          )}
        </span>
      ),
      cell: (info) => (
        <span className={`${secondaryTextClass} whitespace-nowrap`}>
          {(info.getValue() as string) || '—'}
        </span>
      ),
      size: 120,
    },
    {
      accessorKey: 'registerCourt',
      header: () => (
        <span>
          {t(
            `search.table.${columnLabelKey('registerCourt', 'registerCourt', country)}`,
          )}
        </span>
      ),
      cell: (info) => {
        const row = info.row.original;
        const region = info.getValue() as string | null;
        const label = companyCountryCode(row.registerType, row.euId) === 'CH'
          ? swissCantonLabel(region, language)
          : region;
        return (
          <span
            className={`${secondaryTextClass} truncate block max-w-[120px] lg:max-w-[160px]`}
            title={label || undefined}
          >
            {label || '—'}
          </span>
        );
      },
      size: 140,
    },
    {
      accessorKey: 'kreis',
      header: () => <span>{t('search.table.kreis')}</span>,
      cell: (info) => (
        <span
          className={`${secondaryTextClass} truncate block max-w-[120px] lg:max-w-[160px]`}
          title={(info.getValue() as string) || undefined}
        >
          {(info.getValue() as string) || '—'}
        </span>
      ),
      size: 140,
    },
    {
      accessorKey: 'postalCode',
      header: () => <span>{t('search.table.postalCode')}</span>,
      cell: (info) => (
        <span className={`${secondaryTextClass} whitespace-nowrap`}>
          {(info.getValue() as string) || '—'}
        </span>
      ),
      size: 90,
    },
    {
      accessorKey: 'cpvCodes',
      header: () => <span>{t('search.table.cpvCodes')}</span>,
      cell: (info) => {
        const val = info.getValue() as string[] | null;
        const text = val?.length ? val.join(', ') : '';
        return (
          <span
            className={`${secondaryTextClass} truncate block max-w-[140px] lg:max-w-[200px]`}
            title={text || undefined}
          >
            {text || '—'}
          </span>
        );
      },
      size: 160,
    },
  ];
}
